import {
  DoubleSide,
  FrontSide,
  LineBasicMaterial,
  MeshBasicMaterial,
} from 'three';
import type { Color } from 'three';

//eslint-disable-next-line no-restricted-imports
import { defaultColor } from './colors';
import { PARTVIEWER_SHARED_MATERIAL_POOL } from './renderer/cross-section-utils';

//A cache for double sided mesh material
const doubleSidedMaterialByColor = new WeakMap<Color, MeshBasicMaterial>();

//getDoubleSideMaterial(): gets material from cache if it exists, or creates/caches/returns new material
export const getDoubleSideMaterial = (
  color?: Color | null,
): MeshBasicMaterial => {
  let material: MeshBasicMaterial | undefined = color
    ? doubleSidedMaterialByColor.get(color)
    : undefined;
  if (!material) {
    material = new MeshBasicMaterial({
      color: color ?? defaultColor,
      side: DoubleSide,
    });
    material.userData[PARTVIEWER_SHARED_MATERIAL_POOL] = true;
    doubleSidedMaterialByColor.set(color ?? defaultColor, material);
  } else {
    material.userData[PARTVIEWER_SHARED_MATERIAL_POOL] = true;
  }
  return material;
};

const frontSidedMaterialByColor = new WeakMap<Color, MeshBasicMaterial>();

//getFrontSideMaterial(): gets material from cache if it exists, or creates/caches/returns new material
export const getFrontSideMaterial = (
  color?: Color | null,
): MeshBasicMaterial => {
  let material: MeshBasicMaterial | undefined = color
    ? frontSidedMaterialByColor.get(color)
    : undefined;
  if (!material) {
    material = new MeshBasicMaterial({
      color: color ?? defaultColor,
      side: FrontSide,
    });
    material.userData[PARTVIEWER_SHARED_MATERIAL_POOL] = true;
    frontSidedMaterialByColor.set(color ?? defaultColor, material);
  } else {
    material.userData[PARTVIEWER_SHARED_MATERIAL_POOL] = true;
  }
  return material;
};

const basicMaterialByColor = new WeakMap<Color, MeshBasicMaterial>();

//getMeshBasicMaterial(): gets material from cache if it exists, or creates/caches/returns new material
export const getMeshBasicMaterial = (
  color?: Color | null,
): MeshBasicMaterial => {
  let material: MeshBasicMaterial | undefined = color
    ? basicMaterialByColor.get(color)
    : undefined;
  if (!material) {
    material = new MeshBasicMaterial({
      color: color ?? defaultColor,
    });
    material.userData[PARTVIEWER_SHARED_MATERIAL_POOL] = true;
    basicMaterialByColor.set(color ?? defaultColor, material);
  } else {
    material.userData[PARTVIEWER_SHARED_MATERIAL_POOL] = true;
  }
  return material;
};

//A cache of line material
const lineMaterialByColorAndLineWidth = new WeakMap<
  Color,
  Map<number | undefined, LineBasicMaterial>
>();

//getLineMaterial(): gets material from cache if it exists, or creates/caches/returns new LineBasicMaterial
export const getLineMaterial = (
  color?: Color | null,
  linewidth?: number,
): LineBasicMaterial => {
  let lineMaterialByLineWidth:
    | Map<number | undefined, LineBasicMaterial>
    | undefined = lineMaterialByColorAndLineWidth.get(color ?? defaultColor);
  if (!lineMaterialByLineWidth) {
    lineMaterialByLineWidth = new Map();
    lineMaterialByColorAndLineWidth.set(
      color ?? defaultColor,
      lineMaterialByLineWidth,
    );
  }

  let material: LineBasicMaterial | undefined =
    lineMaterialByLineWidth.get(linewidth);

  if (!material) {
    material = new LineBasicMaterial({
      color: color ?? defaultColor,
    });
    material.userData[PARTVIEWER_SHARED_MATERIAL_POOL] = true;
    lineMaterialByLineWidth.set(linewidth, material);
  } else {
    material.userData[PARTVIEWER_SHARED_MATERIAL_POOL] = true;
  }
  return material;
};
